import { tracking } from '@iwoca/frontend-tracking-library';
import { Modal, Button } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { RootState } from '@iwProvider/configureStore';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { UploadSVG } from '../../../../svgComponents/uploadSVG';
import { DocumentStatus } from '../../../modals/uploadDocumentsModal/documentUploader/DocumentUploader';
import messages from '../../../modals/uploadDocumentsModal/documentUploader/DocumentUploader.messages';
import styles from '../../../modals/uploadDocumentsModal/documentUploader/DocumentUploader.module.css';
import { UPLOAD_DOCUMENT } from '../../../modals/uploadDocumentsModal/UploadDocumentsModal.actions';

const MAX_FILE_SIZE = 1073741274; // 1GB

const mapStateToProps = ({ uploadDocumentsModal }: RootState) =>
    uploadDocumentsModal; // we get documentsStatus from here

const mapDispatchToProps = (dispatch: Dispatch) => ({
    uploadDocument: (
        documents: File[],
        documentType: {
            label: string;
            value: string;
        },
        feedbackIds?: number[] | null,
        applicationId?: string
    ) =>
        dispatch({
            type: UPLOAD_DOCUMENT,
            documents,
            documentType,
            feedbackIds,
            applicationId
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const UploadGewerbescheinModal = ({
    uploadDocument,
    documentsStatus,
    isOpen,
    onClose
}: { isOpen: boolean; onClose: () => void } & PropsFromRedux) => {
    const gewerbescheinDocType = {
        label: 'tradeCertificate',
        value: 'trade certificate'
    };
    const onDrop = (acceptedFiles: File[]) => {
        uploadDocument(acceptedFiles, gewerbescheinDocType);
    };

    const handleModalCloseAndTracking = (closingAction: string) => {
        onClose();
        tracking.ampli.uploadGewerbescheinModalClosed({
            closing_action: closingAction
        });
    };
    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                handleModalCloseAndTracking('Clicked out');
            }}
            closeIcon={false}
            headerBar={true}
            title={
                <FormattedMessage
                    id="_.funding_requirements.upload_gewerbeschein_modal_title"
                    defaultMessage="Upload Gewerbeschein"
                />
            }
            className="max-w-[800px]"
        >
            <div>
                <div className={styles.title}>
                    <FormattedMessage
                        id="_.funding_requirements.upload_gewerbeschein_modal_header"
                        defaultMessage="Upload your trade license as PDF"
                    />
                </div>

                <div className={styles.caption}>
                    <Text>
                        <FormattedMessage
                            id="_.funding_requirements.upload_gewerbeschein_modal_caption_info"
                            defaultMessage="• The trade license is issued by your local trade office and confirms when your business was officially started."
                        />
                    </Text>
                    <Text>
                        <FormattedMessage
                            id="_.funding_requirements.upload_gewerbeschein_modal_caption_file_size"
                            defaultMessage="• Files must not exceed 50MB in size."
                        />
                    </Text>
                    <Text>
                        <FormattedMessage
                            id="_.funding_requirements.upload_gewerbeschein_modal_caption_password_protected_files"
                            defaultMessage="• Ensure that the files are not password protected (or share the password with us)."
                        />
                    </Text>
                </div>

                <div className={styles.uploadedFiles}>
                    {documentsStatus.map((document, idx) => (
                        <DocumentStatus key={idx} document={document} />
                    ))}
                </div>

                <Dropzone
                    accept={{ 'application/pdf': ['.pdf'] }}
                    maxSize={MAX_FILE_SIZE}
                    onDrop={onDrop}
                >
                    {({ open, getRootProps, getInputProps }) => (
                        <div
                            className={styles.dropzone}
                            {...getRootProps({
                                onClick: (evt) => evt.stopPropagation()
                            })}
                        >
                            <input type="file" {...getInputProps()} />
                            <div className={styles.dropzoneContent}>
                                <UploadSVG />
                                <div className={styles.dropzoneMessage}>
                                    <FormattedMessage
                                        {...messages.dragAndDropMessage}
                                    />
                                </div>
                                <button
                                    className={styles.browseFile}
                                    onClick={() => open()}
                                >
                                    <FormattedMessage
                                        {...messages.browseFile}
                                    />
                                </button>
                            </div>
                        </div>
                    )}
                </Dropzone>
                <div className="flex justify-center gap-5xl">
                    <Button
                        onClick={() => {
                            handleModalCloseAndTracking('Back');
                        }}
                        size="large"
                        variant="tertiary"
                    >
                        <FormattedMessage
                            id="_.funding_requirements.upload_gewerbeschein_modal_button_back"
                            defaultMessage="Back"
                        />
                    </Button>
                    <Button
                        onClick={() => {
                            handleModalCloseAndTracking('Confirm');
                        }}
                        size="large"
                        variant="primary"
                    >
                        <FormattedMessage
                            id="_.funding_requirements.upload_gewerbeschein_modal_button_confirm"
                            defaultMessage="Confirm"
                        />
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(UploadGewerbescheinModal);
